import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Check, Phone } from "lucide-react";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import TestimonialsSection from "../components/TestimonialsSection";

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 },
};

const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.15,
    },
  },
};

// Real project images - verified mapping
const images = {
  // Hero - use the restored blue tile (After 2)
  hero: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/iqdzyqzo_After%202.jpeg",
  // Blue tile calcium pair (teal mosaic pool)
  blueTileBefore: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/fpgzjpp3_Before%202.jpeg",
  blueTileAfter: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/iqdzyqzo_After%202.jpeg",
  // Stone/masonry pair - SWAPPED based on visual inspection (After 1 file has staining, Before 1 file is cleaner)
  stoneBefore: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/926zc2jx_After%201.jpeg",
  stoneAfter: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/v9fe3x5w_Before%201.jpeg",
  // Pool waterline pair (pool prep/calcium on waterline tile)
  poolPrepBefore: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/uz4ov2h2_Before%203.jpeg",
  poolPrepAfter: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/lncc0qq7_After%203.jpeg",
  // Pool prep action shot - worker blasting pool edge
  poolPrepAction: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/qkfxh1xt_unnamed%281%29.jpg",
  // Worker in pool photo for commercial section
  workerInPool: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/qsdq0hzv_IMG_6615.jpeg",
  // Industrial/oil rig blasting
  industrialBlasting: "https://customer-assets.emergentagent.com/job_media-blasting-dark/artifacts/n0l2zy5o_478AF081-9B7D-41F1-9192-EA57D9B9898A.jpeg",
};

// Hero Section
const HeroSection = () => {
  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center">
      {/* Background Image - Restored blue tile with improved visibility */}
      <div className="absolute inset-0 z-0">
        <img
          src={images.hero}
          alt="Restored pool tile"
          className="w-full h-full object-cover opacity-60 brightness-110 contrast-105"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/60" />
      </div>

      <div className="container-custom relative z-10 pt-32 pb-20 md:pt-40 md:pb-32">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="visible"
          className="max-w-4xl"
        >
          <motion.span
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="label-accent block mb-6"
          >
            Austin, Texas
          </motion.span>

          <motion.h1
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-heading font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-text-primary leading-[1] tracking-tight mb-8"
          >
            Pool Tile Calcium Removal
            <br />
            <span className="text-text-secondary">&amp; Precision Media Blasting</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-text-secondary text-lg md:text-xl max-w-2xl leading-relaxed mb-10"
          >
            Austin's trusted surface restoration specialists. Fast quotes, proven results. Residential pools to commercial projects—we restore tile, stone, and metal surfaces without damage.
          </motion.p>

          <motion.div
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-wrap gap-4"
          >
            <Link
              to="/quote"
              data-testid="hero-quote-cta"
              className="btn-primary flex items-center gap-2"
            >
              Get Free Quote
              <ArrowUpRight className="w-4 h-4" />
            </Link>
            <a
              href="tel:+15129888629"
              data-testid="hero-phone-cta"
              className="btn-secondary flex items-center gap-2"
            >
              <Phone className="w-4 h-4" />
              (512) 988-8629
            </a>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
        >
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-text-secondary to-transparent" />
        </motion.div>
      </div>
    </section>
  );
};

// Services Overview Section
const ServicesSection = () => {
  const services = [
    {
      title: "Pool Tile Calcium Removal",
      description: "Remove years of hard water deposits and calcium buildup. We restore your pool tiles to original condition without damaging surfaces or grout.",
      image: images.blueTileAfter,
      hasImage: true,
    },
    {
      title: "Stone & Masonry Restoration",
      description: "Restore natural stone, brick, and masonry surfaces. Gentle media blasting that cleans deeply without the surface damage caused by pressure washing.",
      image: images.stoneAfter,
      hasImage: true,
    },
    {
      title: "Pool Prep & Surface Blasting",
      description: "Prepare pool surfaces for refinishing, replastering, or new coatings. Complete surface preparation that ensures proper adhesion.",
      image: images.poolPrepAction,
      hasImage: true,
    },
    {
      title: "Specialty & Commercial",
      description: "Fleet prep, architectural surfaces, industrial equipment, custom stencil etching. We handle projects at any scale with the same precision.",
      image: images.industrialBlasting,
      hasImage: true,
    },
  ];

  return (
    <section data-testid="services-section" className="section-spacing bg-surface">
      <div className="container-custom">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
        >
          <motion.span variants={fadeUp} className="label-accent block mb-4">
            Our Services
          </motion.span>
          <motion.h2
            variants={fadeUp}
            className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-text-primary mb-16 max-w-3xl"
          >
            Surface restoration for those who demand results
          </motion.h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group"
              data-testid={`service-card-${index}`}
            >
              {service.hasImage ? (
                <div className="relative aspect-[3/2] overflow-hidden mb-5">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-background/30 group-hover:bg-background/10 transition-colors duration-500" />
                </div>
              ) : (
                <div className="relative aspect-[3/2] overflow-hidden mb-5 bg-surface-highlight border border-white/5 flex items-center justify-center">
                  <div className="text-center p-6">
                    <span className="label-accent block mb-2">Coming Soon</span>
                    <p className="text-text-secondary text-sm">Commercial project photos</p>
                  </div>
                </div>
              )}
              <h3 className="font-heading font-bold text-xl md:text-2xl text-text-primary mb-3">
                {service.title}
              </h3>
              <p className="text-text-secondary leading-relaxed">
                {service.description}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center"
        >
          <Link
            to="/services"
            data-testid="services-view-all"
            className="inline-flex items-center gap-2 text-accent font-mono uppercase tracking-widest text-sm hover:text-text-primary transition-colors"
          >
            View All Services
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// Before/After Proof Section
const BeforeAfterSection = () => {
  return (
    <section data-testid="before-after-section" className="section-spacing">
      <div className="container-custom">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="mb-16"
        >
          <motion.span variants={fadeUp} className="label-accent block mb-4">
            Real Results
          </motion.span>
          <motion.h2
            variants={fadeUp}
            className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-text-primary max-w-3xl"
          >
            See the transformation
          </motion.h2>
        </motion.div>

        <div className="space-y-12">
          {/* First comparison: Blue Tile Calcium Removal */}
          <div data-testid="comparison-blue-tile">
            <div className="mb-6">
              <h3 className="font-heading font-bold text-xl text-text-primary mb-2">
                Pool Tile Calcium Removal
              </h3>
              <p className="text-text-secondary">Austin, TX — Residential Pool</p>
            </div>
            <BeforeAfterSlider
              beforeImage={images.blueTileBefore}
              afterImage={images.blueTileAfter}
            />
          </div>

          {/* Second comparison: Stone/Masonry Restoration */}
          <div data-testid="comparison-stone">
            <div className="mb-6">
              <h3 className="font-heading font-bold text-xl text-text-primary mb-2">
                Stone & Masonry Restoration
              </h3>
              <p className="text-text-secondary">Austin, TX — Exterior Wall</p>
            </div>
            <BeforeAfterSlider
              beforeImage={images.stoneBefore}
              afterImage={images.stoneAfter}
            />
          </div>

          {/* Third comparison: Pool Prep / Waterline Tile */}
          <div data-testid="comparison-pool-prep">
            <div className="mb-6">
              <h3 className="font-heading font-bold text-xl text-text-primary mb-2">
                Pool Prep & Waterline Tile
              </h3>
              <p className="text-text-secondary">Austin, TX — Pool Surface Preparation</p>
            </div>
            <BeforeAfterSlider
              beforeImage={images.poolPrepBefore}
              afterImage={images.poolPrepAfter}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Commercial Capability Section - kept compact to not overpower core proof
const CommercialSection = () => {
  const capabilities = [
    "Pool Tile Restoration",
    "Surface Prep for Replastering",
    "Stone & Masonry Cleaning",
    "Commercial Projects",
  ];

  return (
    <section data-testid="commercial-section" className="py-20 md:py-28 bg-surface">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="label-accent block mb-4">Also Serving</span>
            <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-text-primary mb-5">
              Residential and commercial projects
            </h2>
            <p className="text-text-secondary leading-relaxed mb-6">
              Whether you're a homeowner or a property manager, we deliver the same precision and professionalism on every project.
            </p>

            <ul className="grid grid-cols-2 gap-3 mb-6">
              {capabilities.map((item) => (
                <li key={item} className="flex items-center gap-3 text-text-secondary">
                  <Check className="w-4 h-4 text-accent flex-shrink-0" />
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>

            <Link
              to="/industries"
              data-testid="commercial-industries-link"
              className="inline-flex items-center gap-2 text-accent font-mono uppercase tracking-widest text-sm hover:text-text-primary transition-colors"
            >
              View All Industries
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src={images.workerInPool}
                alt="Media blasting in progress"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-4 -left-4 bg-accent p-4 md:p-6">
              <span className="font-heading font-bold text-3xl md:text-4xl text-white block">100+</span>
              <span className="text-white/80 text-xs font-mono uppercase tracking-wider">Projects</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Process Section
const ProcessSection = () => {
  const steps = [
    {
      number: "01",
      title: "Request Quote",
      description: "Tell us about your project. We respond within 24 hours with a detailed estimate.",
    },
    {
      number: "02",
      title: "Site Assessment",
      description: "We evaluate the surface, discuss your goals, and confirm the right approach.",
    },
    {
      number: "03",
      title: "Precision Blasting",
      description: "Our team executes with the appropriate media for your specific surface type.",
    },
    {
      number: "04",
      title: "Final Walkthrough",
      description: "We inspect the results together and ensure complete satisfaction.",
    },
  ];

  return (
    <section data-testid="process-section" className="section-spacing">
      <div className="container-custom">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="mb-16"
        >
          <motion.span variants={fadeUp} className="label-accent block mb-4">
            How It Works
          </motion.span>
          <motion.h2
            variants={fadeUp}
            className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-text-primary max-w-3xl"
          >
            Four steps to restore surfaces
          </motion.h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              data-testid={`process-step-${index}`}
              className="border-l border-white/10 pl-6"
            >
              <span className="font-mono text-accent text-sm">{step.number}</span>
              <h3 className="font-heading font-bold text-xl text-text-primary mt-2 mb-3">
                {step.title}
              </h3>
              <p className="text-text-secondary leading-relaxed text-sm">
                {step.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Featured Work Gallery - varied images, no unnecessary repetition
const FeaturedWorkSection = () => {
  const works = [
    { image: images.blueTileAfter, title: "Pool Tile Restoration", category: "Calcium Removal" },
    { image: images.poolPrepAfter, title: "Pool Prep Complete", category: "Pool Prep" },
    { image: images.stoneAfter, title: "Stone Wall Cleaning", category: "Surface Restoration" },
    { image: images.poolPrepBefore, title: "Waterline Calcium", category: "Before" },
  ];

  return (
    <section data-testid="featured-work-section" className="section-spacing bg-surface">
      <div className="container-custom">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12"
        >
          <div>
            <motion.span variants={fadeUp} className="label-accent block mb-4">
              Recent Work
            </motion.span>
            <motion.h2
              variants={fadeUp}
              className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-text-primary"
            >
              Project gallery
            </motion.h2>
          </div>
          <motion.div variants={fadeUp}>
            <Link
              to="/work"
              data-testid="featured-work-view-all"
              className="inline-flex items-center gap-2 text-accent font-mono uppercase tracking-widest text-sm hover:text-text-primary transition-colors"
            >
              View All Projects
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {works.map((work, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              data-testid={`featured-work-${index}`}
              className="group relative aspect-square overflow-hidden cursor-pointer"
            >
              <img
                src={work.image}
                alt={work.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-background/60 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col justify-end p-4">
                <span className="text-accent text-xs font-mono uppercase tracking-wider">
                  {work.category}
                </span>
                <span className="text-text-primary font-heading font-bold text-sm">
                  {work.title}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Closing CTA Section
const ClosingCTASection = () => {
  return (
    <section data-testid="closing-cta-section" className="section-spacing">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto"
        >
          <span className="label-accent block mb-6">Ready to Start?</span>
          <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-text-primary mb-6">
            Get your free quote today
          </h2>
          <p className="text-text-secondary text-lg mb-10 max-w-xl mx-auto">
            Describe your project and receive a detailed estimate within 24 hours. No obligation.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/quote"
              data-testid="closing-cta-quote"
              className="btn-primary inline-flex items-center gap-2"
            >
              Request Free Quote
              <ArrowUpRight className="w-4 h-4" />
            </Link>
            <a
              href="tel:+15129888629"
              data-testid="closing-cta-phone"
              className="btn-secondary inline-flex items-center gap-2"
            >
              <Phone className="w-4 h-4" />
              Call Now
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Main Home Component
const Home = () => {
  return (
    <div data-testid="home-page">
      <HeroSection />
      <ServicesSection />
      <BeforeAfterSection />
      <CommercialSection />
      <ProcessSection />
      <TestimonialsSection />
      <FeaturedWorkSection />
      <ClosingCTASection />
    </div>
  );
};

export default Home;
